import { ArrowLeft20, Close20, Search16 } from '@carbon/icons-react';
import { isNaN } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { ProjectFileGroup } from 'api/project-files/types';
import { RoutePaths } from 'constant';
import { useIsTablet } from 'hooks/use-media-query';
import { useTranslate } from 'hooks/use-translation';
import { handleDownload } from 'utils/attachment';
import { isImageUrlFromExtension } from 'utils/files';
import { SearchInput } from '../../components/cp-input';
import { useGetBackground } from '../../types/utils';
import { CpWidgetProps } from '../../types/widget';
import { useCustomerPortalHashMap } from '../../utils';
import { StyledBackWrapper } from '../all-files/cp-all-files';
import { ListOrGridMode } from '../all-files/types';
import { BackIconWrapper } from '../chat/components';
import { CpSectionWrapper } from '../common';
import { CpWidgetHeader } from '../common/header';
import { CpAccountFilesForLaptop } from './cp-account-files.laptop';
import { CpAccountFilesForMobile } from './cp-account-files.mobile';
import { ator, CpAccountFilesData } from './types';

export const CpAccountFiles: React.FC<CpWidgetProps> = ({
  widget,
  data: _data,
  projectId,
  disabled,
  onRuntimeConfigChange,
}) => {
  const router = useHistory();
  const isResponsive = useIsTablet();
  const background = useGetBackground(widget);
  const data = useMemo(() => _data as CpAccountFilesData, [_data]);
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState<ListOrGridMode>(ListOrGridMode.Grid);
  const { hashMap, setHashMap, removeKey } = useCustomerPortalHashMap();
  const translations = useTranslate();
  const _currentFolder: ProjectFileGroup | undefined = useMemo(() => {
    const split = hashMap?.acc_folder?.split('.');
    const isCurrentProjectANumber = !isNaN(Number(split?.[0]));
    const isCurrentFolderANumber = !isNaN(Number(split?.[1]));

    if (isCurrentFolderANumber && isCurrentProjectANumber) {
      const folderProjectId = Number(split?.[0]);
      const folderId = Number(split?.[1]);

      const project = data?.folders.find(
        (folder) => folder.project.projectId === folderProjectId,
      );

      const folder = project?.folders?.find((f) => f.folderId === folderId);

      if (folder) {
        return {
          ...folder,
          folderName: search?.trim()
            ? translations('search_results')
            : folder.folderName,
        };
      }

      return folder;
    }

    if (isCurrentProjectANumber && split?.[1] === ator(Number(split?.[0]))) {
      const otherAttachments = data?.folders?.find(
        (folder) => folder.project.projectId === Number(split?.[0]),
      )?.otherAttachments;

      return {
        folderName: translations('project.task.attachments.title', {
          count: otherAttachments?.length ?? 0,
        }),
        projectId: Number(split?.[0]),
        folderId: ator(Number(split?.[0])) as unknown as number,
        attachments: otherAttachments?.map((a) => a?.attachment) ?? [],
      } as ProjectFileGroup;
    }

    if (isResponsive) {
      return undefined;
    }

    return data?.folders?.at(0)?.folders?.at(0);
  }, [data, hashMap?.acc_folder, isResponsive, search, translations]);

  const currentFolder = useMemo(() => {
    if (search?.trim() && _currentFolder) {
      return {
        ..._currentFolder,
        folderName: translations('search_results'),
      };
    }

    return _currentFolder;
  }, [search, _currentFolder, translations]);

  const allAttachments = useMemo(() => {
    return (
      data?.folders
        ?.flatMap((e) =>
          e?.folders?.concat([
            {
              folderId: ator(e?.project?.projectId) as unknown as number,
              folderName: translations('project.task.attachments.title', {
                count: e?.otherAttachments?.length ?? 0,
              }),
              projectId: e.project.projectId,
              attachments: e.otherAttachments?.map((a) => a?.attachment) ?? [],
            },
          ] as ProjectFileGroup[]),
        )
        ?.flatMap((e) => e?.attachments) ?? []
    );
  }, [data?.folders, translations]);

  const currentAttachments = useMemo(() => {
    if (search?.trim()) {
      return (
        allAttachments?.filter((e) =>
          e?.name?.toLowerCase?.()?.includes(search?.trim?.().toLowerCase?.()),
        ) ?? []
      );
    }

    const attachments = currentFolder?.attachments;

    return (
      attachments?.filter((e) =>
        e?.name?.toLowerCase?.()?.includes(search?.trim?.().toLowerCase?.()),
      ) ?? []
    );
  }, [allAttachments, currentFolder, search]);

  const onFolderClick = useCallback(
    (
      args:
        | {
            projectId: number;
            folderId: number;
          }
        | undefined,
    ) => {
      if (!args || !args?.folderId || !args?.projectId) {
        removeKey('acc_folder');
        setSearch('');

        return;
      }
      setHashMap('acc_folder', `${args.projectId}.${args.folderId}`);
    },
    [removeKey, setHashMap],
  );

  const onFileClick = useCallback(
    (attachmentId: number) => {
      const attachment = allAttachments?.find(
        (a) => a?.attachmentId === attachmentId,
      );

      if (!attachment) {
        return;
      }

      if (isImageUrlFromExtension(attachment?.name)) {
        router.push(
          RoutePaths.customerPortalAttachmentById
            .replace(':projectId', projectId.toString())
            .replace(':attachmentId', attachmentId.toString()),
        );
      } else {
        // open in a new tab
        window.open(attachment?.location, '_blank');
      }
    },
    [allAttachments, projectId, router],
  );

  return (
    <CpSectionWrapper
      background={background}
      paddingY={
        widget?.widgetDefinition?.styles?.paddingY as number | undefined
      }
      isDisabled={disabled}
    >
      {isResponsive && currentFolder ? (
        <>
          <StyledBackWrapper>
            <BackIconWrapper onClick={() => onFolderClick(undefined)}>
              <ArrowLeft20 />
            </BackIconWrapper>
            <CpWidgetHeader.Title autoAdjustColor>
              {currentFolder?.folderName}
            </CpWidgetHeader.Title>
          </StyledBackWrapper>
        </>
      ) : (
        <CpWidgetHeader>
          <CpWidgetHeader.Title autoAdjustColor>
            {data.title}
          </CpWidgetHeader.Title>
          <CpWidgetHeader.Description autoAdjustColor>
            {data.description}
          </CpWidgetHeader.Description>
        </CpWidgetHeader>
      )}
      {!isResponsive || currentFolder ? (
        <SearchInput
          prefix={<Search16 />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={translations('search')}
          suffix={
            search ? <Close20 onClick={() => setSearch('')} /> : undefined
          }
        />
      ) : null}
      {isResponsive ? (
        <CpAccountFilesForMobile
          refetch={() => onRuntimeConfigChange?.({})}
          handleDownload={handleDownload}
          currentAttachments={currentAttachments}
          currentFolder={currentFolder}
          data={data}
          mode={mode}
          onFolderClick={onFolderClick}
          search={search}
          setMode={setMode}
          onFileClick={onFileClick}
          projectId={projectId}
        />
      ) : (
        <CpAccountFilesForLaptop
          refetch={() => onRuntimeConfigChange?.({})}
          handleDownload={handleDownload}
          currentAttachments={currentAttachments}
          currentFolder={currentFolder}
          data={data}
          mode={mode}
          onFolderClick={onFolderClick}
          search={search}
          setMode={setMode}
          onFileClick={onFileClick}
          projectId={projectId}
        />
      )}
    </CpSectionWrapper>
  );
};
